import React from 'react';
import { Meta } from '../../../../styled components/Typography';
import ListViewBookingCount from '../../ListViewBookingCount';
import ListViewSetToggle from '../../ListViewSetToggle/ListViewSetToggle';
import {
  ChildBooking,
  ChildBookingHeader,
  ChildBookingLabel,
  ChildBookingList,
  ChildBookingName,
  NestedChildBookings
} from './ListViewChildBooking.style';
import { bookingTimeRange } from '../../../../libs/listViewPresentation';
import type {
  ListViewBooking,
  ListViewDiff,
  ListViewSetCollapse,
  TimeFormatSetting
} from '../../../types';

interface ListViewChildBookingProps {
  child: ListViewBooking;
  setCollapse: ListViewSetCollapse;
  statusByID: ListViewDiff['statusByID'];
  timeFormatSetting: TimeFormatSetting;
}

/* A member booking of a set, shown inside the set's card. A member that is a
   set itself opens and closes on its own, and lists its own members
   underneath, indented one step further - sets nest without a depth limit, so
   this component renders itself again for every level. Only the diff markers
   are shown on a member; the temporal greying/highlight is left to the card of
   the top-level booking. */
const ListViewChildBooking = ({
  child,
  setCollapse,
  statusByID,
  timeFormatSetting
}: ListViewChildBookingProps) => {
  const marker = statusByID[child.id];
  const isSet = child.children.length > 0;
  const expanded = setCollapse.isSetExpanded(child.id);
  const name = (
    <>
      <ChildBookingName $deleted={marker?.status === 'deleted'}>
        {child.resourceName}
      </ChildBookingName>
      <ListViewBookingCount booking={child} />
    </>
  );
  return (
    <>
      <ChildBooking
        $status={marker?.status}
        data-test-list-view-child
        data-test-row-status={marker ? marker.status : undefined}
      >
        <ChildBookingHeader>
          <ChildBookingLabel>
            {isSet ? (
              <ListViewSetToggle
                expanded={expanded}
                onToggle={() => setCollapse.onToggleSet(child.id)}
                setName={child.resourceName}
              >
                {name}
              </ListViewSetToggle>
            ) : (
              name
            )}
          </ChildBookingLabel>
          <Meta tw="shrink-0 ml-8" $color="gray light">
            {bookingTimeRange(child, timeFormatSetting)}
          </Meta>
        </ChildBookingHeader>
        {child.comment ? (
          <Meta as="p" tw="mt-2 mb-0">
            {child.comment}
          </Meta>
        ) : null}
      </ChildBooking>
      {isSet && expanded && (
        <NestedChildBookings>
          <ChildBookingList>
            {child.children.map((nestedChild) => (
              <ListViewChildBooking
                key={nestedChild.id}
                child={nestedChild}
                setCollapse={setCollapse}
                statusByID={statusByID}
                timeFormatSetting={timeFormatSetting}
              />
            ))}
          </ChildBookingList>
        </NestedChildBookings>
      )}
    </>
  );
};

export default ListViewChildBooking;
